import React, { useId, useMemo } from 'react';
import { ACCENT_COLOR } from '../constants/colors';
import {
    currentDescriptionChapter,
    descriptionPlaybackRate,
    descriptionTimestamp,
    parseEpisodeDescription,
} from '../utils/episodeDescription';

const EpisodeDescription = ({
    curTime = 0,
    description = '',
    durationSeconds,
    playbackRate = 1,
    showsPlaybackProgress = false,
    onTimestampPlay,
}) => {
    const segments = useMemo(() => parseEpisodeDescription(description), [description]);
    const chapterDescriptionId = useId();
    const rate = descriptionPlaybackRate(playbackRate);
    const chapter = currentDescriptionChapter(segments, curTime, durationSeconds, rate, showsPlaybackProgress);
    let countdownShown = false;
    let openParentheses = 0;

    return (
        <div className='episodeDescription'>
            <p>{segments.map((segment, index) => {
                // Source timestamp wrappers have already been removed. Retained
                // prose parentheses determine which countdown delimiters fit.
                const text = segment.type === 'text' ? segment.text : segment.type === 'link' ? segment.display : '';
                for (const character of text) {
                    if (character === '(') openParentheses += 1;
                    else if (character === ')') openParentheses = Math.max(0, openParentheses - 1);
                }
                if (segment.type === 'text') return <React.Fragment key={index}>{segment.text}</React.Fragment>;
                if (segment.type === 'link') return (
                    <a key={index} href={segment.url} title={segment.url}
                        aria-label={`${segment.display} (${segment.url})`}
                        target='_blank' rel='noopener noreferrer' style={{ color: ACCENT_COLOR }}>
                        {segment.display}
                    </a>
                );
                const display = descriptionTimestamp(segment.seconds / rate);
                const showCountdown = !countdownShown && chapter?.startSeconds === segment.seconds;
                if (showCountdown) countdownShown = true;
                const delimiters = openParentheses > 0 ? ['[', ']'] : ['(', ')'];
                const following = segments[index + 1];
                const preserveFollowingGap = !following || (following.type === 'text' && /^[\s\p{P}]/u.test(following.text));
                const TimestampGroup = showCountdown ? 'span' : React.Fragment;
                return (
                    <TimestampGroup key={index} {...(showCountdown ? { className: 'episodeDescriptionActiveChapter' } : {})}>
                        <button type='button' className={`episodeDescriptionTimestamp${showCountdown ? ' episodeDescriptionTimestampBadge' : ''}`}
                            style={showCountdown ? { '--episode-chapter-progress': `${chapter.progress * 100}%` } : { color: ACCENT_COLOR }}
                            aria-label={`Jump to ${display}`}
                            aria-describedby={showCountdown ? chapterDescriptionId : undefined}
                            onClick={() => onTimestampPlay?.(segment.seconds)}>
                            {showCountdown ? <>
                                <span className='episodeDescriptionTimestampFill' aria-hidden='true' />
                                <span className='episodeDescriptionTimestampLabel'>{display}</span>
                            </> : display}
                        </button>
                        {showCountdown && (
                            <span className={`episodeDescriptionTimeLeft${preserveFollowingGap ? ' episodeDescriptionTimeLeftPreserveGap' : ''}`}>
                                <span className='episodeDescriptionTimeLeftSizer' aria-hidden='true'>
                                    {`${delimiters[0]}-${chapter.maximumRemainingTime}${delimiters[1]}`}
                                </span>
                                <span className='episodeDescriptionTimeLeftValue' aria-hidden='true'>
                                    {`${delimiters[0]}-${chapter.timeRemaining}${delimiters[1]}`}
                                </span>
                                <span className='visuallyHidden' id={chapterDescriptionId}>
                                    {`${chapter.timeRemaining} remaining in chapter, ${Math.round(chapter.progress * 100)}% complete`}
                                </span>
                            </span>
                        )}
                    </TimestampGroup>
                );
            })}</p>
        </div>
    );
};

export default EpisodeDescription;
